import { gql, useQuery } from '@apollo/client';
import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import { Badge, Button, Heading, IconButton } from '@apollo/orbit';
import { clamp, range } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly/hooks';

import { ChecksFilters } from '../../checksPage/checksFilters/ChecksFilters';
import { CheckWorkflowTaskItemFragment } from '../../checksPage/checkWorkflowsListPage/checkWorkflowItem/checkWorkflowTaskItem/CheckWorkflowTaskItem';
import { GraphRef } from '../../hooks/useGraphRef';
import { proposalChecksListRouteConfig } from '../../routes';

import {
  ChecksForRevisionCard,
  ChecksForRevisionCardWorfklowFragment,
} from './ChecksForRevisionCard';
import {
  EmptyChecksPage,
  NoMatchChecksPage,
  RetryChecksPage,
} from './EmptyChecksPage';

const PROPOSAL_CHECK_PAGE_SIZE = 5;
const MIN_TOTAL_WITH_ALL_PAGE_NUMBERS_VISIBLE = 10;
const BUFFER_AROUND_CURRENT_VISIBLE_PAGE = 2;

const ProposalsCheckWorkflowItemFragment = gql`
  fragment ProposalsCheckWorkflowItemFragment on CheckWorkflow {
    id
    status
    proposalRevision {
      id
    }
    tasks {
      id
      ...CheckWorkflowTaskItemFragment
    }
    ...ChecksForRevisionCardWorfklowFragment
  }
  ${CheckWorkflowTaskItemFragment}
  ${ChecksForRevisionCardWorfklowFragment}
`;

export const ProposalChecks = ({ graphRef }: { graphRef: GraphRef }) => {
  const {
    author,
    createdBy,
    status,
    subgraph,
    page = 1,
    shouldHighlightLatest,
  } = useRouteParams(proposalChecksListRouteConfig);
  const selectedAuthors = typeof author === 'string' ? [author] : author;
  const selectedCreatedBy =
    typeof createdBy === 'string' ? [createdBy] : createdBy;
  const selectedSubgraphs =
    typeof subgraph === 'string' ? [subgraph] : subgraph;
  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );

  const { data, loading, error } = useQuery<
    GraphQLTypes.ProposalChecksQuery,
    GraphQLTypes.ProposalChecksQueryVariables
  >(
    gql`
      # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
      query ProposalChecksQuery(
        $graphId: ID!
        $graphVariant: String!
        $limit: Int!
        $offset: Int!
        $filter: CheckFilterInput
        $proposalsUseHasWarnings: Boolean!
      ) {
        service(id: $graphId) {
          id
          totalCheckWorkflowCount(filter: $filter)
          checkWorkflows(filter: $filter, limit: $limit, offset: $offset) {
            id
            ...ProposalsCheckWorkflowItemFragment
          }
          variant(name: $graphVariant) {
            id
            launchHistoryLength
            proposal {
              id
              latestRevision {
                id
              }
            }
          }
        }
      }
      ${ProposalsCheckWorkflowItemFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      pollInterval: 30_000,
      variables: {
        graphId: graphRef.graphId,
        graphVariant: graphRef.graphVariant,
        offset: PROPOSAL_CHECK_PAGE_SIZE * (page - 1),
        limit: PROPOSAL_CHECK_PAGE_SIZE,
        filter: {
          variants: [graphRef.graphVariant],
          includeProposalChecks: true,
          authors: selectedAuthors,
          createdBy: selectedCreatedBy?.map((createdById) => ({
            actorId: createdById,
            type: GraphQLTypes.ActorType.USER, // Always should be a user in proposals, no service is publishing to proposals
          })),
          status,
          subgraphs: selectedSubgraphs,
        },
        proposalsUseHasWarnings,
      },
      fetchPolicy: 'network-only',
    },
  );

  const proposal = data?.service?.variant?.proposal;

  const totalCount = data?.service?.totalCheckWorkflowCount ?? 0;
  const totalPages = Math.ceil(totalCount / PROPOSAL_CHECK_PAGE_SIZE) || 1;

  const hasPreviousRevisions =
    (data?.service?.variant?.launchHistoryLength ?? 0) > 1;
  const latestRevisionId = proposal?.latestRevision?.id ?? '';

  const visiblePages = useMemo(() => {
    if (totalPages <= MIN_TOTAL_WITH_ALL_PAGE_NUMBERS_VISIBLE) {
      return range(1, totalPages + 1);
    }

    const startPage = clamp(
      page -
        BUFFER_AROUND_CURRENT_VISIBLE_PAGE -
        (page === totalPages - 4 ? 1 : 0),
      2,
      totalPages - 4,
    );
    const endPage = clamp(
      page +
        BUFFER_AROUND_CURRENT_VISIBLE_PAGE +
        (page === totalPages - 4 ? 1 : 0),
      5,
      totalPages - 1,
    );

    return [1, ...range(startPage, endPage + 1), totalPages];
  }, [page, totalPages]);

  const location = useLocation();

  const [highlightFinished, setHighlightFinished] = useState(false);
  const shouldHighlight = shouldHighlightLatest === 'true';

  useEffect(() => {
    // remove highlight state after 5s
    if (shouldHighlight) {
      const timeout = setTimeout(() => {
        setHighlightFinished(true);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [shouldHighlight]);

  if (loading) {
    return <Loading />;
  }

  if (error || !proposal) {
    return <ErrorPage error={error ?? Error('Something went wrong')} />;
  }

  const filtersAreNotEmpty =
    (selectedAuthors && isMinLengthArray(1, selectedAuthors)) ||
    (selectedSubgraphs && isMinLengthArray(1, selectedSubgraphs)) ||
    status;

  return (
    <PageLayout>
      <div className="p-8">
        <div className="flex items-center justify-between">
          <Heading size="2xl">Recent checks</Heading>
          <ChecksFilters className="ml-6 flex h-full items-center" />
        </div>
        {totalCount === 0 ? (
          filtersAreNotEmpty ? (
            <NoMatchChecksPage />
          ) : hasPreviousRevisions ? (
            <RetryChecksPage
              latestRevisionId={latestRevisionId}
              graphRef={graphRef}
            />
          ) : (
            <EmptyChecksPage />
          )
        ) : (
          <>
            <div>
              {data.service?.checkWorkflows
                .filter(
                  (
                    checkWorkflow,
                  ): checkWorkflow is typeof checkWorkflow & {
                    proposalRevision: NonNullable<
                      (typeof checkWorkflow)['proposalRevision']
                    >;
                  } => !!checkWorkflow.proposalRevision,
                )
                ?.map((checkWorkflow, index) => {
                  return (
                    <div key={checkWorkflow.proposalRevision.id}>
                      <div className="grid grid-cols-9 gap-x-2 pb-3 pt-8 ">
                        <Heading as="h3" size="lg" className="col-span-8">
                          <Tooltip label={checkWorkflow.proposalRevision.id}>
                            <span>
                              Revision{' '}
                              {checkWorkflow.proposalRevision.id.substring(
                                0,
                                8,
                              )}
                            </span>
                          </Tooltip>
                          {checkWorkflow.proposalRevision.id ===
                            latestRevisionId && (
                            <Badge variant="info" className="ml-2">
                              Latest Revision
                            </Badge>
                          )}
                        </Heading>
                      </div>
                      <ChecksForRevisionCard
                        shouldHighlightSubgraphs={
                          !highlightFinished && shouldHighlight && index === 0
                        }
                        checkWorkflow={checkWorkflow}
                      />
                    </div>
                  );
                })}
            </div>
            {!loading && totalCount > 0 && (
              <nav
                aria-label="pagination"
                className="mx-auto my-6 flex items-center justify-center space-x-3"
              >
                <IconButton
                  variant="hidden"
                  icon={<IconChevronLeft />}
                  aria-label="Previous page"
                  isDisabled={page === 1}
                  as={Link}
                  to={proposalChecksListRouteConfig.locationFrom({
                    location,
                    patch: {
                      page: page - 1,
                    },
                  })}
                />
                {visiblePages.map((value, i) => {
                  const shouldShowEllipsis =
                    i > 0 && value - (visiblePages[i - 1] ?? 0) > 1;
                  return (
                    <React.Fragment key={value}>
                      {shouldShowEllipsis && (
                        <div
                          className="cursor-not-allowed px-2 text-sm font-semibold text-secondary"
                          role="presentation"
                        >
                          ...
                        </div>
                      )}
                      <Button
                        aria-label={`Page ${value}`}
                        variant={page === value ? 'primary' : 'hidden'}
                        as={Link}
                        to={proposalChecksListRouteConfig.locationFrom({
                          location,
                          patch: {
                            page: value,
                          },
                        })}
                        {...(page === value ? { 'aria-current': 'page' } : {})}
                      >
                        {value}
                      </Button>
                    </React.Fragment>
                  );
                })}
                <IconButton
                  variant="hidden"
                  icon={<IconChevronRight />}
                  aria-label="Next page"
                  isDisabled={page === totalPages}
                  as={Link}
                  to={proposalChecksListRouteConfig.locationFrom({
                    location,
                    patch: {
                      page: page + 1,
                    },
                  })}
                />
              </nav>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};
